import { useEffect, useMemo, useRef, useState } from "react";
import * as PR from "../../prime-modules/index";
import { getAllAssets } from "../../genericFunctions/getAssets";
import { useDispatch } from "react-redux";
import { uploadAssetsApi } from "../../services/api";
import { useSelector } from "react-redux";
import {
  commonPMs,
  eSIMCrewPMs,
  eSimplePMs,
} from "./helper";

const PaymentMethods = ({ assets, disableSave }) => {
  const adminData = useSelector((state) => state.adminAuth.adminSessionData);
  const headers = useMemo(() => {
    return { sessionid: adminData.sessionId };
  }, [adminData.sessionId]);
  const dispatch = useDispatch();
  const toast = useRef(null);

  const [paymentMethodsOrder, setPaymentMethodsOrder] = useState([]);
  const [webPaymentMethodsOrder, setWebPaymentMethodsOrder] = useState([]);
  useEffect(() => {
    const paymentMethodsHandler = () => {
      const paymentMethods = assets?.paymentMethods ? JSON.parse(assets?.paymentMethods) : null;
      const webPaymentMethods = assets?.webPaymentMethods ? JSON.parse(assets?.webPaymentMethods) : null;
      let paymentMethodsList = [...commonPMs];
      if (process.env.REACT_APP_NAME === "esimcrew" || process.env.REACT_APP_NAME === "pinoy") {
        paymentMethodsList = [...paymentMethodsList, ...eSIMCrewPMs];
      } else if (process.env.REACT_APP_NAME === "esimple") {
        paymentMethodsList = [...paymentMethodsList, ...eSimplePMs];
      }

      const transformToIdentified = (apiList) => {
        if (!apiList) return [];
        const seen = new Set();
        return apiList.filter((item) => {
          if (!item) return false;
          const key = Object.keys(item)[0];
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return paymentMethodsList.includes(key);
        }).map(item => {
          const key = Object.keys(item)[0];
          return { id: key, value: item[key] };
        });
      };

      setPaymentMethodsOrder(transformToIdentified(paymentMethods));

      if (webPaymentMethods) {
        setWebPaymentMethodsOrder(transformToIdentified(webPaymentMethods));
      } else {
        // Fallback initialized state if webPaymentMethods is null/empty
        const fallbackWebPms = paymentMethodsList.map(pm => ({ id: pm, value: "0" }));
        setWebPaymentMethodsOrder(fallbackWebPms);
      }
    };
    if (assets?.paymentMethods) {
      paymentMethodsHandler();
    }
  }, [assets?.paymentMethods, assets?.webPaymentMethods, dispatch]);

  const itemTemplate = (item, isWeb = false) => {
    if (!item) return null;
    let key = item.id;
    let value = item.value;
    
    const targetOrder = isWeb ? webPaymentMethodsOrder : paymentMethodsOrder;
    const setTargetOrder = isWeb ? setWebPaymentMethodsOrder : setPaymentMethodsOrder;

    return (
      <ul className="payment-ul">
        <li>
          <div>{key}</div>
          <div>
            <PR.InputSwitch
              checked={value !== "0" ? true : false}
              onChange={() => {
                const index = targetOrder.findIndex((currentItem) => currentItem.id === key);
                if (index > -1) {
                  const newOrder = [...targetOrder];
                  newOrder[index] = { 
                    ...newOrder[index], 
                    value: value !== "0" ? "0" : (index + 1).toString() 
                  };
                  setTargetOrder(newOrder);
                }
              }}
            />
          </div>
        </li>
      </ul>
    );
  };

  const formatOrderList = (orderList) => {
    return orderList.map((item, index) => {
      return {
        [item.id]: item.value !== "0" ? (index + 1).toString() : "0"
      };
    });
  };

  const handleSubmit = async (type) => {
    const formData = new FormData();
    formData.append("screen", "settings");
    formData.append("paymentMethods", JSON.stringify(formatOrderList(paymentMethodsOrder)));
    formData.append("webPaymentMethods", JSON.stringify(formatOrderList(webPaymentMethodsOrder)));

    const getCMSResponse = (response) => {
      if (response.result === "SUCCESS") {
        if (response.data === "success") {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: `${type === 'mobile' ? 'Mobile' : 'Web'} Payment Methods updated successfully`,
          });
        }
        getAllAssets(toast, dispatch, headers);
      } else if (response.result === "FAILED" && response.error) {
        const error = response.error;
        toast.current.show({
          severity: error.severity,
          summary: "Error",
          detail: error.errorMsg ? error.errorMsg : error.summary,
        });
      }
    };
    uploadAssetsApi(formData, headers, dispatch, getCMSResponse);
  };

  const paymentOptionsSortHandler = (e, isWeb = false) => {
    const listValue = e?.value || e;
    if (!Array.isArray(listValue)) return;

    const sortPaymentOptions = listValue.map((item, i) => {
      if (!item) return item;
      return {
        ...item,
        value: item.value !== "0" ? (i + 1).toString() : "0",
      };
    });
    
    if (isWeb) {
      setWebPaymentMethodsOrder(sortPaymentOptions);
    } else {
      setPaymentMethodsOrder(sortPaymentOptions);
    }
  };
  return (
    <div className="w-12">
      <PR.Toast ref={toast} />
          <div className="grid mt-5">
      {assets?.paymentMethods ? (
        <>
            <div className="col-12 md:col-6 payment-methods-list">
              <h2 className="mb-3">Mobile Payment Methods</h2>
              <PR.OrderList
                dataKey="id"
                value={paymentMethodsOrder}
                onChange={(e) => paymentOptionsSortHandler(e, false)}
                itemTemplate={(item) => itemTemplate(item, false)}
                dragdrop
              />
              <div className="col-12 mt-3 text-right">
                <PR.Button
                  label="Save Mobile Methods"
                  type="button"
                  className="saveBtn mt-0 custom-ml"
                  onClick={() => handleSubmit('mobile')}
                  disabled={disableSave}
                />
              </div>
            </div>
        </>
      ) : (
        <div className="payment-methods-loading">
          <i className="pi pi-spin pi-spinner"></i>
          <span>Loading</span>
        </div>
      )}
      {assets?.webPaymentMethods ? (
        <>
            <div className="col-12 md:col-6 payment-methods-list">
              <h2 className="mb-3">Web Payment Methods</h2>
              <PR.OrderList
                dataKey="id"
                value={webPaymentMethodsOrder}
                onChange={(e) => paymentOptionsSortHandler(e, true)}
                itemTemplate={(item) => itemTemplate(item, true)}
                dragdrop
              />
              <div className="col-12 mt-3 text-right">
                <PR.Button
                  label="Save Web Methods"
                  type="button"
                  className="saveBtn mt-0 custom-ml"
                  onClick={() => handleSubmit('web')}
                  disabled={disableSave}
                />
              </div>
            </div>
        </>
      ) : (
        <div className="payment-methods-loading">
         
        </div>
      )}
          </div>
    </div>
  );
};

export default PaymentMethods;
